import { StorageNamesEnum } from "@ch/helpers/utils/enums";
import { NotifyInfo } from "@ch/helpers/utils/helpers";
import logoutUser from "@ch/helpers/utils/logoutUser";
import type { TEnvModes } from "@ch/helpers/utils/types";
import { store } from "@ch/redux-store/store";
import { updateAppUserState } from "@ch/redux-store/user/actions";
import axios from "axios";
import localforage from "localforage";

const postHogApiKey = import.meta.env.VITE_POSTHOG_API_KEY as string;
const payStackApiKey = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY as string;
export const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY as string;

export const pushNotificationPublicKey = import.meta.env
  .VITE_PUSH_NOTIFICATION_PUBLIC_KEY as string;
export const NAVIHEALTH_API_KEY = import.meta.env
  .VITE_NAVIHEALTH_API_KEY as string;

// TODO: this will be used in the future
// const envMode = import.meta.env.MODE;
export const envMode: TEnvModes =
  (import.meta.env.VITE_REACT_APP_ENV as TEnvModes) ||
  (process.env.NODE_ENV as TEnvModes);

const config = {
  development: {
    REACT_APP_API_URL: "https://mobile-api-dev.mymdoc.com",
    // REACT_APP_API_URL: "http://localhost:5055",
    SOCKET_IO_URL: "https://mobile-api-dev.mymdoc.com",
    PAYSTACK_API_KEY: payStackApiKey,
    PAYSTACK_DEFAULT_EMAIL: "payment-test@mymdoc.com",
    POSTHOG_API_KEY: postHogApiKey,
    POSTHOG_HOST: "https://us.i.posthog.com",
    REACT_APP_PUSH_NOTIFICATION_PUBLIC_KEY: pushNotificationPublicKey,
  },
  staging: {
    REACT_APP_API_URL: "https://mobile-api-staging.mymdoc.com",
    SOCKET_IO_URL: "https://mobile-api-staging.mymdoc.com",
    PAYSTACK_API_KEY: payStackApiKey,
    PAYSTACK_DEFAULT_EMAIL: "payment-test@mymdoc.com",
    POSTHOG_API_KEY: postHogApiKey,
    POSTHOG_HOST: "https://us.i.posthog.com",
    REACT_APP_PUSH_NOTIFICATION_PUBLIC_KEY: pushNotificationPublicKey,
  },
  production: {
    REACT_APP_API_URL: "https://mobile-api.mymdoc.com",
    SOCKET_IO_URL: "https://mobile-api.mymdoc.com",
    PAYSTACK_API_KEY: payStackApiKey,
    PAYSTACK_DEFAULT_EMAIL: "payment@mymdoc.com",
    POSTHOG_API_KEY: postHogApiKey,
    POSTHOG_HOST: "https://us.i.posthog.com",
    REACT_APP_PUSH_NOTIFICATION_PUBLIC_KEY: pushNotificationPublicKey,
  },
};

const navihealthConfig = {
  development: {
    NAVIHEALTH_BACKEND_API_URL: "https://dev-api.navihealth.ai",
    GOOGLE_PLACES_API_KEY: GOOGLE_API_KEY,
  },
  staging: {
    NAVIHEALTH_BACKEND_API_URL: "https://staging-api.navihealth.ai",
    GOOGLE_PLACES_API_KEY: GOOGLE_API_KEY,
  },
  production: {
    NAVIHEALTH_BACKEND_API_URL: "https://api.navihealth.ai",
    GOOGLE_PLACES_API_KEY: GOOGLE_API_KEY,
  },
};

const kemConfig = {
  development: {
    KEM_BACKEND_API_URL:
      "https://kem-backend-dev-163254254870.us-east1.run.app",
    KEM_SOCKET_HOST: "https://kem-backend-dev-163254254870.us-east1.run.app",
  },
  staging: {
    KEM_BACKEND_API_URL:
      "https://kem-backend-pilot-163254254870.us-east1.run.app",
    KEM_SOCKET_HOST:
      "https://kem-backend-staging-163254254870.us-east1.run.app",
  },
  production: {
    KEM_BACKEND_API_URL:
      "https://kem-backend-pilot-163254254870.us-east1.run.app",
    KEM_SOCKET_HOST: "https://kem-backend-pilot-163254254870.us-east1.run.app",
  },
};

export const enableAnalytics = ["production"].includes(envMode);

export const { REACT_APP_API_URL, SOCKET_IO_URL } = config[envMode];

export const { NAVIHEALTH_BACKEND_API_URL, GOOGLE_PLACES_API_KEY } =
  navihealthConfig[envMode];

export const { KEM_BACKEND_API_URL, KEM_SOCKET_HOST } = kemConfig[envMode];

export const { PAYSTACK_API_KEY, PAYSTACK_DEFAULT_EMAIL } = config[envMode];

export const {
  POSTHOG_API_KEY,
  POSTHOG_HOST,
  REACT_APP_PUSH_NOTIFICATION_PUBLIC_KEY,
} = config[envMode];

export const httpClient = axios.create({
  baseURL: REACT_APP_API_URL,
});

// axios interceptor to handle 401 errors from KEM endpoints
httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const isKemEndpoint =
      error?.config?.baseURL?.includes("kem-backend") ||
      error?.config?.url?.includes("/transcribe") ||
      error?.config?.url?.includes("/kem-message");

    // handle 401 errors from KEM endpoints - logout user
    if (error?.response?.status === 401 && isKemEndpoint) {
      // dynamically import to avoid circular dependency
      const { default: handleKemAuthError } =
        await import("@ch/pages/KemPage/utils/handleKemAuthError");
      handleKemAuthError();
    }

    return Promise.reject(error);
  },
);

export interface RefreshedTokens {
  accessToken: string;
  refreshToken?: string;
}

let refreshPromise: Promise<RefreshedTokens | null> | null = null;

/**
 * Single in-flight refresh: all concurrent callers share the same promise.
 * Persists accessToken to localforage + Redux on success.
 * Deliberately ignores returned refreshToken to avoid rotation so KEM has a consistent refresh token.
 * Logs out only when the refresh endpoint itself returns 401 (session invalid).
 */
export async function getRefreshedTokens(): Promise<RefreshedTokens | null> {
  if (refreshPromise !== null) {
    return refreshPromise;
  }

  refreshPromise = (async (): Promise<RefreshedTokens | null> => {
    try {
      const refreshToken = await localforage.getItem<string>(
        StorageNamesEnum.REFRESH_TOKEN,
      );

      if (!refreshToken) {
        return null;
      }

      const { data } = await axios.get<{
        accessToken?: string;
        refreshToken?: string;
      }>(
        `${REACT_APP_API_URL}/users/refresh-token?token=${refreshToken}&source=web`,
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        },
      );

      const accessToken = data?.accessToken;
      const newRefreshToken = data?.refreshToken;

      if (!accessToken) {
        return null;
      }

      await localforage.setItem(StorageNamesEnum.ACCESS_TOKEN, accessToken);

      if (newRefreshToken) {
        await localforage.setItem(
          StorageNamesEnum.REFRESH_TOKEN,
          newRefreshToken,
        );
      }

      httpClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

      store?.dispatch(
        updateAppUserState({
          accessToken,
          refreshToken: newRefreshToken,
        }),
      );

      return { accessToken, refreshToken: newRefreshToken };
    } catch (err: any) {
      if (err?.response?.status === 401) {
        NotifyInfo("Please login again");
        logoutUser();
      }
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function httpRequest<T>(request: () => Promise<T>): Promise<T> {
  // eslint-disable-next-line no-async-promise-executor, @typescript-eslint/no-misused-promises
  return new Promise(async (resolve, reject) => {
    try {
      const response = await request();
      resolve(response);
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.status === 401) {
        const originalRequest = error.response?.config || error.body?.config;

        const tokens = await getRefreshedTokens();

        if (tokens && originalRequest) {
          try {
            const authRequest = {
              ...originalRequest,
              headers: {
                ...originalRequest.headers,
                Authorization: `Bearer ${tokens.accessToken}`,
              },
            };
            const response: any = await axios(authRequest);
            resolve(response?.data ?? response);
          } catch (retryError) {
            reject(retryError);
          }
        } else {
          reject(error);
        }
      } else {
        reject(error);
      }
    }
  });
}

class TimeoutError extends Error {
  response: {
    data: {
      message: string;
    };
    status: number;
  };

  constructor(message: string, status: number) {
    super(message);
    this.name = "TimeoutError";
    this.response = {
      data: { message },
      status,
    };
  }
}

function timeoutPromise<T>(ms: number, promise: Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      const error = new TimeoutError(
        "We experienced a timeout, please try again.",
        409,
      );
      reject(error);
    }, ms);
    promise.then(
      (res) => {
        clearTimeout(timeoutId);
        resolve(res);
      },
      (err) => {
        clearTimeout(timeoutId);
        reject(err);
      },
    );
  });
}

export async function apiWrapper<T>(request: () => Promise<T>) {
  const response = await timeoutPromise(60000, httpRequest(request));
  return response;
}

export const handleAccessToken = async () => {
  const token = await localforage.getItem<string>(
    StorageNamesEnum.ACCESS_TOKEN,
  );
  httpClient.defaults.headers.common.Authorization = token
    ? `Bearer ${token}`
    : null;
};
